import SpaceForButton from '@/components/_common/SpaceForButtons';
import {
  useInstallmentReverse,
  useInstallmentRenegotiate,
  useAccountPayableListGrouping,
} from '@/core/domain/financial/AccountPayable/AccountPayable.hooks';
import {
  InstallmentReverseFormValues,
  InstallmentReverseRequest,
  InstallmentRenegotiateFormValues,
  InstallmentRenegotiateRequest,
  InstallmentSettleFormValues,
  InstallmentSituation,
  AccountPayable,
} from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import { Button, Dropdown, Form, Menu } from 'antd';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDisclosure } from '@/hooks';
import SettledSingleModal from '@/components/Registry/BillToPay/ReleaseForm/SettledSingleModal';
import { useHistory } from 'react-router-dom';
import SettledPartiallyModal from '@/components/Registry/BillToPay/ReleaseForm/SettledPartiallyModal';
import { DateTimeFormat } from '@/utils/moment';
import SplitInstallmentModal from '../../SplitInstallmentModal';
import ReverseInstallmentModal from '../../ReverseInstallmentModal';
import RenegotiateInstallmentModal from '../../RenegotiateInstallmentModal';
import ButtonBack from '@/components/_common/ButtonBack';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { UseDisclosureActions } from '@/hooks/useDisclosure';

import './style.less';
import AccountPayableListGroupingModal from '../../AccountPayableListGroupingModal';
import { AxiosError } from 'axios';
import { handleInstallmentAlreadyEdited } from '@/core/domain/financial/AccountPayable/AccountPayable.errors';
import usePageSearchHistory from '@/hooks/pageSearchHistory';
import { useAccountPayableContext } from '@/pages/FinancialPages/BillToPayPage/ReleasesPage/ReleasesDetailsPage';

interface Props {
  disabledButtonBack: boolean;
  handleSave: () => Promise<AccountPayable | undefined>;
}

const AccontPayableButtonsRepeated: React.FC<Props> = ({
  disabledButtonBack,
  handleSave,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const [form] = Form.useForm<InstallmentSettleFormValues>();

  const { currentInstallment, accountPayable } = useAccountPayableContext();

  const installment = accountPayable?.installmentPayables[currentInstallment];
  const installmentList = accountPayable?.installmentPayables;

  const { getSearch } = usePageSearchHistory();
  const path = '/app/financial/billToPay/releaseBillToPay';
  const search = getSearch(path) || '';
  const pathWithSearch = `${path}${search}`;

  const reverseInstallmentMutation = useInstallmentReverse();
  const renegotiateInstallment = useInstallmentRenegotiate();
  const listGrouping = useAccountPayableListGrouping(
    installment?.accountPayableId,
  );

  const [modalSettleSingle, modalSettleSingleAction] = useDisclosure();
  const [modalSettlePartially, modalSettlePartiallyAction] = useDisclosure();
  const [modalSplitInstallment, modalSplitInstallmentAction] = useDisclosure();
  const [modalReverseInstallment, modalReverseInstallmentAction] =
    useDisclosure();
  const [modalRenegotiateInstallment, modalRenegotiateInstallmentAction] =
    useDisclosure();
  const [modalListGrouping, modalListGroupingAction] = useDisclosure();

  const [loadingButtons, setLoadingButtons] = useState({
    parcel: false,
    renegotiate: false,
    settleInstallment: false,
  });

  const handleRenegotiateInstallment = async (
    values?: InstallmentRenegotiateFormValues,
  ) => {
    try {
      if (installment && values) {
        const obj: InstallmentRenegotiateRequest = {
          ...values,
          installmentPayableId: installment.id,
          accountId: installment.accountPayableId,
          fromThatDate: values.fromThatDate.format(DateTimeFormat),
          quantityInstallments: Number(values.quantityInstallments),
          currentSituation: installment.situation,
        };

        const response = await renegotiateInstallment.mutateAsync(obj);

        history.push(
          `/app/financial/billToPay/releaseBillToPay/detail/accountPayableId/${response.accountId}/installmentPayableId/${response.installmentId}`,
        );

        modalRenegotiateInstallmentAction.close();
      }
    } catch (err: unknown) {
      const error = err as AxiosError;

      if (error.response?.data) {
        if (handleInstallmentAlreadyEdited(error)) {
          modalSplitInstallmentAction.close();
        }
      }
    }
  };

  const handleReverseInstallment = async (
    values?: InstallmentReverseFormValues,
  ) => {
    if (installment && values) {
      const obj: InstallmentReverseRequest = {
        ...values,
        id: installment.accountPayableId,
        installmentPayableId: installment.id,
        reversalDate: values.reversalDate.format(DateTimeFormat),
        supplierId: accountPayable.supplierId,
      };

      await reverseInstallmentMutation.mutateAsync(obj);

      modalReverseInstallmentAction.close();
    }
  };

  const handleCancel = () => {
    form.resetFields();

    modalSettleSingleAction.close();
    modalSettlePartiallyAction.close();
    modalSplitInstallmentAction.close();
  };

  const [settleData, setSettleData] = useState({});

  useEffect(() => {
    setSettleData({
      id: installment?.accountPayableId,
      installmentPayableId: installment?.id,
      installmentValue: installment?.installmentValue,
      bankAccountId: installment?.bankAccountId,
      credit: installment?.credit,
      currentSituation: installment?.situation,
      discountIncrease: installment?.discountIncrease,
      documentTypeId: installment?.documentTypeId,
    });
  }, [installment]);

  const handleChangeAndOpenModal = async (
    actions: UseDisclosureActions,
    loading: string,
  ) => {
    if (!disabledButtonBack) {
      setLoadingButtons((prev) => ({ ...prev, [loading]: true }));

      const result = await handleSave();

      if (result?.installmentPayable) {
        setSettleData((prev) => ({
          ...prev,
          installmentValue: result.installmentPayable.installmentValue,
          bankAccountId: result.installmentPayable.bankAccountId,
          credit: result.installmentPayable.credit,
          discountIncrease: result.installmentPayable.discountIncrease,
          documentTypeId: result.installmentPayable.documentTypeId,
        }));
      }
      setLoadingButtons((prev) => ({ ...prev, [loading]: false }));
    }

    actions.open();
  };

  return (
    <>
      <SpaceForButton>
        <ButtonBack path={pathWithSearch} disabled={disabledButtonBack} />

        <Button
          block
          type="primary"
          onClick={() =>
            history.push('/app/financial/billToPay/releaseBillToPay/create')
          }
          disabled={
            !hasPermissions([
              Authorities.FINANCIAL_ACCOUNT_PAYABLE_GENERATE_ACCOUNTS_PAYABLE,
            ])
          }
        >
          {intl.formatMessage({
            id: 'financial.billsToPay.detail.button.newAccountPayable',
          })}
        </Button>

        {installment?.situation === InstallmentSituation.open &&
          installmentList?.length === 1 && (
            <Button
              block
              type="primary"
              loading={loadingButtons.parcel}
              onClick={() =>
                handleChangeAndOpenModal(modalSplitInstallmentAction, 'parcel')
              }
            >
              {intl.formatMessage({
                id: 'financial.billsToPay.detail.button.parcel',
              })}
            </Button>
          )}

        {installment?.situation === InstallmentSituation.open &&
          installmentList?.length !== 1 && (
            <Button
              block
              type="primary"
              loading={
                renegotiateInstallment.isLoading || loadingButtons.renegotiate
              }
              onClick={() =>
                handleChangeAndOpenModal(
                  modalRenegotiateInstallmentAction,
                  'renegotiate',
                )
              }
            >
              {intl.formatMessage({
                id: 'financial.billsToPay.detail.button.renegotiate',
              })}
            </Button>
          )}

        {installment?.situation !== InstallmentSituation.settled &&
        installment?.situation !== InstallmentSituation.canceled ? (
          <Dropdown.Button
            className="dropdown-button"
            disabled={
              !hasPermissions([
                Authorities.FINANCIAL_ACCOUNT_PAYABLE_INSTALLMENTS_SETTLEMENT,
              ])
            }
            overlay={
              <Menu>
                <Menu.Item
                  key="settlePartiallyAndSingle"
                  onClick={() =>
                    handleChangeAndOpenModal(
                      modalSettlePartiallyAction,
                      'settleInstallment',
                    )
                  }
                >
                  {intl.formatMessage({
                    id: 'financial.billsToPay.detail.button.partiallyLiquidate',
                  })}
                </Menu.Item>
              </Menu>
            }
            onClick={() =>
              handleChangeAndOpenModal(
                modalSettleSingleAction,
                'settleInstallment',
              )
            }
            loading={loadingButtons.settleInstallment}
            type="primary"
          >
            {intl.formatMessage({
              id: 'financial.billsToPay.detail.button.settleInstallment',
            })}
          </Dropdown.Button>
        ) : (
          <>
            {installment?.situation !== InstallmentSituation.canceled && (
              <Button
                block
                type="primary"
                onClick={modalReverseInstallmentAction.open}
              >
                {intl.formatMessage({
                  id: 'financial.billsToPay.detail.button.reverseInstallment',
                })}
              </Button>
            )}
          </>
        )}

        {!!listGrouping.data?.length && (
          <Button block type="primary" onClick={modalListGroupingAction.open}>
            Contas Agrupadas
          </Button>
        )}
      </SpaceForButton>

      <SettledSingleModal
        open={modalSettleSingle}
        onCancel={handleCancel}
        data={settleData}
      />

      <SettledPartiallyModal
        open={modalSettlePartially}
        onCancel={handleCancel}
        data={settleData}
      />

      <SplitInstallmentModal
        open={modalSplitInstallment}
        onCancel={handleCancel}
      />

      <ReverseInstallmentModal
        open={modalReverseInstallment}
        onCancel={modalReverseInstallmentAction.close}
        onCallBackValues={handleReverseInstallment}
        isLoading={reverseInstallmentMutation.isLoading}
      />

      <RenegotiateInstallmentModal
        open={modalRenegotiateInstallment}
        onCancel={modalRenegotiateInstallmentAction.close}
        installmentList={installmentList}
        onCallBackValues={handleRenegotiateInstallment}
        isLoading={renegotiateInstallment.isLoading}
      />

      <AccountPayableListGroupingModal
        onCancel={modalListGroupingAction.close}
        open={modalListGrouping}
        listGroupingData={listGrouping.data}
      />
    </>
  );
};

export default AccontPayableButtonsRepeated;
